import type { FC } from 'react';

import { AlertTriangle, X } from 'lucide-react';
import { type Control, Controller } from 'react-hook-form';

import type { FilterFormValues } from '@shared/api/findings/filter-form.schema';
import { FacetedFilterOperator, FacetedFilterOperatorLabel } from '@shared/api/findings/findings.types';
import { type FacetedFilter } from '@shared/api/findings/findings.types';
import { Badge, Input, Separator } from '@shared/components/ui';
import { GenericButton, GenericSelect, GenericTooltip } from '@shared/components/ui/custom';
import { toHumanReadableLabel } from '@shared/utils/label.utils';

import { FacetedMultiSelect } from './FacetedMultiSelect';

interface FilterConditionRowProps {
  fieldKey: string;
  filter: FacetedFilter;
  isLastRow: boolean;
  control: Control<FilterFormValues>;
  onDropdownClose: (fieldKey: string) => void;
  updateFiltersFromForm: () => void;
  toggleField: (field: string) => void;
  zeroCount?: boolean;
  addedWithZeroValues?: boolean;
}

export const FilterConditionRow: FC<FilterConditionRowProps> = ({
  fieldKey,
  filter,
  isLastRow,
  control,
  onDropdownClose,
  updateFiltersFromForm,
  toggleField,
  zeroCount = false,
  addedWithZeroValues = false,
}) => {
  const displayLabel = toHumanReadableLabel(filter.label);
  return (
    <div className="flex items-center gap-2 mb-2 px-4" data-testid="filter-condition-row-root">
      <Badge
        className={`w-48 px-2 py-2.5 h-10 rounded-md text-sm border flex items-center gap-1 font-normal ${zeroCount ? 'bg-orange-900 border-orange-500 text-orange-200 cursor-default' : 'bg-neutral-800 border-input  text-white cursor-default pointer-events-none'}`}
        tabIndex={zeroCount ? 0 : -1}
        aria-disabled={zeroCount}
        data-testid="filter-condition-row-badge"
      >
        <span className="truncate" data-testid="filter-condition-row-label">
          {displayLabel}
        </span>
        {zeroCount && (
          <GenericTooltip
            content={
              addedWithZeroValues
                ? 'The current set of conditions returns zero results. To get more results either change or remove one of the filters.'
                : 'This condition will return zero results. You can still enter a new value to prove no activity'
            }
            side="right"
            align="center"
            className="z-3000"
            data-testid="filter-condition-row-tooltip"
          >
            <span className="ml-1 flex items-center cursor-pointer">
              <AlertTriangle className="w-4 h-4 text-orange-400 cursor-help" />
            </span>
          </GenericTooltip>
        )}
      </Badge>
      <Controller
        name={`fieldValues.${fieldKey}.operator` as const}
        control={control}
        disabled={addedWithZeroValues}
        render={({ field }) => (
          <GenericSelect
            options={[
              { value: FacetedFilterOperator.IS, label: FacetedFilterOperatorLabel[FacetedFilterOperator.IS] },
              { value: FacetedFilterOperator.NOT, label: FacetedFilterOperatorLabel[FacetedFilterOperator.NOT] },
            ]}
            value={field.value}
            onChange={field.onChange}
            onClose={updateFiltersFromForm}
            disabled={addedWithZeroValues}
            className="w-20 font-normal"
            data-testid="filter-condition-row-operator"
          />
        )}
      />
      <span className="flex-1">
        <Controller
          name={`fieldValues.${fieldKey}.values` as const}
          control={control}
          render={({ field }) =>
            addedWithZeroValues ? (
              <Input
                type="text"
                placeholder={`Enter ${displayLabel}`}
                value={field.value?.[0] ?? ''}
                onChange={(e) => field.onChange([e.target.value])}
                onBlur={updateFiltersFromForm}
                data-testid="filter-condition-row-input"
              />
            ) : (
              <FacetedMultiSelect
                options={filter.options}
                value={field.value ?? []}
                onChange={field.onChange}
                placeholder={`Select ${displayLabel}`}
                onClose={() => onDropdownClose(fieldKey)}
                data-testid="filter-condition-row-multiselect"
              />
            )
          }
        />
      </span>
      <div className="flex items-center justify-center h-10 w-9 shrink-0 ml-0.5 mr-1">
        {!isLastRow && (
          <>
            <Separator orientation="vertical" className="mx-2 h-6 bg-muted" />
            <span className="text-sm text-muted-foreground">AND</span>
          </>
        )}
      </div>
      <span className="ml-2">
        <GenericButton
          icon={X}
          variant="ghost"
          className="text-gray-400 hover:text-red-400"
          aria-label={`Remove condition ${displayLabel}`}
          onClick={() => toggleField(fieldKey)}
          data-testid="filter-condition-row-remove"
        />
      </span>
    </div>
  );
};
